import { EntityId, EntitySelect, EntitySelectEntity } from '@factorialco/f0-react/dist/experimental'
import { useQuery } from 'modules/core/hooks/useQuery'
import { AvatarNameSelectorDocument } from 'generated/resources/graphql'
import i18n from 'modules/core/lib/i18n'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { groupByTeams } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/groups/byTeams'
import { useEffect, useMemo, useRef, useState } from 'react'

interface Props {
  label: string
  defaultValue?: EntityId
  teamId?: string
  onChange: (value: EntitySelectEntity | null) => void
  disabled?: boolean
}

function findEntityBySubId(
  entities: EntitySelectEntity[],
  subId: EntityId
): EntitySelectEntity | undefined {
  for (const team of entities) {
    const match = team.subItems?.filter((subItem) => Number(subId) === Number(subItem.subId))
    if (match && match.length > 0) {
      return { ...team, subItems: match }
    }
  }
  return undefined
}

export const AssigneeSelect = ({
  label,
  defaultValue,
  teamId,
  onChange,
  disabled = false,
}: Props) => {
  const { company } = useCurrentSession()

  const [expandedElements, setExpandedElements] = useState<EntityId[]>([])
  const [selected, setSelected] = useState<EntitySelectEntity | undefined>()
  const prevDefaultValue = useRef<EntityId | undefined>(defaultValue)

  const { data: employeeData, loading: employeesLoading } = useQuery(AvatarNameSelectorDocument, {
    variables: {
      companyId: company.id,
      ids: [],
      includeTeams: true,
    },
  })
  const teamsEntities = useMemo(() => {
    const all = groupByTeams(employeeData?.employees.employeeNames, [], expandedElements)
    if (teamId == null) return all
    return all.filter((team) => String(team.id) === teamId)
  }, [employeeData?.employees.employeeNames, expandedElements, teamId])

  // Sync selection when defaultValue changes or teamsEntities load
  useEffect(() => {
    const valueChanged = prevDefaultValue.current !== defaultValue
    prevDefaultValue.current = defaultValue

    if (!defaultValue) {
      if (valueChanged) setSelected(undefined)
      return
    }

    const resolved = findEntityBySubId(teamsEntities, defaultValue)
    if (resolved) {
      setSelected(resolved)
    } else if (valueChanged) {
      // defaultValue changed but entity not found (e.g. data not loaded yet) — clear stale
      setSelected(undefined)
    }
  }, [defaultValue, teamsEntities])

  const onItemExpandedChange = (id: EntityId, expanded: boolean) => {
    if (expanded) {
      setExpandedElements([id].concat(expandedElements))
    } else {
      setExpandedElements(expandedElements.filter((el) => el !== id))
    }
  }

  const handleSelect = (selection: EntitySelectEntity | null) => {
    setSelected(selection ?? undefined)
    onChange(selection)
  }

  return (
    <EntitySelect
      hideLabel={!label}
      label={label}
      disabled={disabled}
      singleSelector
      loading={employeesLoading}
      entities={teamsEntities}
      selectedGroup={'teams'}
      selectedItemsCopy=""
      onItemExpandedChange={onItemExpandedChange}
      groups={[{ label: 'Teams', value: 'teams' }]}
      onGroupChange={() => {}}
      notFoundTitle={i18n.t('employee_selector.not_found.title')}
      notFoundSubtitle={i18n.t('employee_selector.not_found.subtitle')}
      selectedEntities={!selected ? [] : [selected]}
      onSelect={handleSelect}
    />
  )
}
